import type { Message, MessageStats, StreamChunk, TimelinePart, ToolCallInfo } from "./api";
import { useStore } from "./store";

export type StreamAccum = {
  content: string;
  reasoning: string;
  toolCalls: ToolCallInfo[];
  timeline: TimelinePart[];
  finalStats?: MessageStats;
};

export const EMPTY_ACCUM: StreamAccum = {
  content: "",
  reasoning: "",
  toolCalls: [],
  timeline: [],
};

function appendText(timeline: TimelinePart[], kind: "reasoning" | "content", text: string): TimelinePart[] {
  const last = timeline[timeline.length - 1];
  if (last && last.kind === kind) {
    return [...timeline.slice(0, -1), { kind, text: last.text + text }];
  }
  return [...timeline, { kind, text }];
}

export type ChunkResult = {
  accum: StreamAccum;
  patch?: Partial<Message>;
  stats?: MessageStats;
  toolCallEnd?: ToolCallInfo;
};

export function applyChunk(accum: StreamAccum, chunk: StreamChunk): ChunkResult {
  if (chunk.snapshot) {
    const next: StreamAccum = {
      content: chunk.snapshot.content,
      reasoning: chunk.snapshot.reasoning,
      toolCalls: chunk.snapshot.toolCalls,
      timeline: chunk.snapshot.timeline,
      finalStats: chunk.snapshot.stats ?? accum.finalStats,
    };
    return {
      accum: next,
      patch: {
        content: next.content,
        reasoning: next.reasoning || undefined,
        toolCalls: next.toolCalls.length > 0 ? next.toolCalls : undefined,
        timeline: next.timeline.length > 0 ? next.timeline : undefined,
        activeToolCall: chunk.snapshot.activeToolCall,
        activeToolCallChars: chunk.snapshot.activeToolCallChars,
        stats: chunk.snapshot.stats,
      },
    };
  }
  if (chunk.toolCalls) {
    const next: StreamAccum = { ...accum, toolCalls: chunk.toolCalls };
    return { accum: next, patch: { toolCalls: chunk.toolCalls, activeToolCall: undefined } };
  }
  if (chunk.toolCallStart) {
    return { accum, patch: { activeToolCall: chunk.toolCallStart.tool, activeToolCallChars: 0 } };
  }
  if (chunk.toolCallProgress) {
    return { accum, patch: { activeToolCallChars: chunk.toolCallProgress.argumentsLength } };
  }
  if (chunk.toolCallEnd) {
    const timeline: TimelinePart[] = [...accum.timeline, { kind: "tool", call: chunk.toolCallEnd }];
    return { accum: { ...accum, timeline }, patch: { timeline }, toolCallEnd: chunk.toolCallEnd };
  }
  if (chunk.stats) {
    return {
      accum: { ...accum, finalStats: chunk.stats },
      patch: { stats: chunk.stats },
      stats: chunk.stats,
    };
  }
  if (chunk.reasoning) {
    const timeline = appendText(accum.timeline, "reasoning", chunk.reasoning);
    const next: StreamAccum = { ...accum, reasoning: accum.reasoning + chunk.reasoning, timeline };
    return { accum: next, patch: { reasoning: next.reasoning, timeline } };
  }
  if (typeof chunk.content === "string" && chunk.content) {
    const timeline = appendText(accum.timeline, "content", chunk.content);
    const next: StreamAccum = { ...accum, content: accum.content + chunk.content, timeline };
    return { accum: next, patch: { content: next.content, timeline } };
  }
  return { accum };
}

type ToolEndEffect = (call: ToolCallInfo) => void;

const refreshFoldersAndConversations: ToolEndEffect = () => {
  const s = useStore.getState();
  void s.refreshFolders();
  void s.refreshConversations();
};

const refreshArtifacts: ToolEndEffect = () => {
  void useStore.getState().refreshArtifacts();
};

const recordActivatedSkill: ToolEndEffect = (call) => {
  const result = call.result as Record<string, unknown> | undefined;
  const skillInfo = result && typeof result === "object" ? (result.skill as Record<string, unknown> | undefined) : undefined;
  const newId = typeof skillInfo?.id === "string" ? skillInfo.id : null;
  if (!newId) return;
  const current = useStore.getState().activeActivatedSkillIds;
  if (!current.includes(newId)) {
    void useStore.getState().setActiveActivatedSkills([...current, newId]);
  }
};

export const TOOL_END_EFFECTS: Record<string, ToolEndEffect> = {
  create_folder: refreshFoldersAndConversations,
  rename_folder: refreshFoldersAndConversations,
  delete_folder: refreshFoldersAndConversations,
  move_chat: refreshFoldersAndConversations,
  artifact_create: refreshArtifacts,
  artifact_write: refreshArtifacts,
  artifact_edit: refreshArtifacts,
  artifact_delete: refreshArtifacts,
  activate_skill: recordActivatedSkill,
};

export function runToolEndEffect(call: ToolCallInfo): void {
  TOOL_END_EFFECTS[call.tool]?.(call);
}
